/**
 * airan-ntn — FedAVG Implementation
 */

import { FLStrategy, ModelWeights, TrainSample, SimConfig, UserEquipment } from '../types.js';
import { random, randn } from '../channel.js';
import { cloneWeights, addWeights, scaleWeights, paramCount, softmax, forward, crossEntropyLoss, accuracy, sgdStep } from '../model.js';

export class FedAVG implements FLStrategy {
  readonly name = 'FedAVG' as const;

  localTrain(
    ue: UserEquipment,
    globalWeights: ModelWeights,
    data: TrainSample[],
    config: SimConfig
  ): void {
    if (data.length === 0) return;

    // Clone global weights for local training
    const localWeights = cloneWeights(globalWeights);

    // Local SGD epochs
    for (let epoch = 0; epoch < config.localEpochs; epoch++) {
      sgdStep(localWeights, data, config.learningRate, 0, null);
    }

    ue.localWeights = localWeights;
  }

  aggregate(
    globalWeights: ModelWeights,
    participants: UserEquipment[],
    _allData: Map<number, TrainSample[]>,
    config: SimConfig
  ): ModelWeights {
    if (participants.length === 0) return globalWeights;

    // Weighted average by data size
    let totalSamples = 0;
    for (const ue of participants) {
      totalSamples += ue.dataSize;
    }

    const newWeights = {
      w: new Float32Array(globalWeights.w.length).fill(0),
      b: new Float32Array(globalWeights.b.length).fill(0),
      numFeatures: globalWeights.numFeatures,
      numClasses: globalWeights.numClasses,
      version: globalWeights.version + 1
    };

    for (const ue of participants) {
      const weight = ue.dataSize / totalSamples;
      for (let i = 0; i < globalWeights.w.length; i++) {
        newWeights.w[i] += ue.localWeights.w[i] * weight;
      }
      for (let i = 0; i < globalWeights.b.length; i++) {
        newWeights.b[i] += ue.localWeights.b[i] * weight;
      }
    }

    return newWeights;
  }
}